import * as React from "react";
import * as moment from "moment";
import {getBrandFromPolicyNumber, WarningBanner, resourcesHelper} from "@ferry/portal-core";
import {D_MMM_YYYY_DATE_FORMAT, MissedAnnualPaymentInfo, Policy} from "@ferry/journey-services";
import {getAmountWithDecimalDigits} from "../../CommonFunctions";
import {MissedPaymentLink} from "../links/MissedPaymentLink";

interface IProps {
    policy: Policy;
    reuseCard: boolean;
}

export class RenewalMissedPaymentBanner extends React.Component<IProps> {

    public render() {
        if (!this.props.policy?.annualPolicyInfo?.missedAnnualPaymentInfo) {
            return <></>
        }

        const missedPaymentInfo: MissedAnnualPaymentInfo = this.props.policy.annualPolicyInfo.missedAnnualPaymentInfo;

        const now: moment.Moment = moment();
        const invoiceDueDate: moment.Moment = moment(missedPaymentInfo.invoiceDueDate);
        const secondAttemptDate: moment.Moment = moment(missedPaymentInfo.secondAttemptDate);
        const cancellationDate: moment.Moment = moment(missedPaymentInfo.cancellationDate);

        const secondAttemptString: string = moment(secondAttemptDate).format(D_MMM_YYYY_DATE_FORMAT);
        const cancellationString: string = moment(cancellationDate).format(D_MMM_YYYY_DATE_FORMAT);

        const brandName: string = this.props.policy.policyNumber ?
            getBrandFromPolicyNumber(this.props.policy.policyNumber)! :
            resourcesHelper.THEME_NAME;

        // policy should be cancelled if 14 days have passed, so not handling that scenario within this banner
        if (now.isSameOrAfter(moment(secondAttemptDate)) && missedPaymentInfo.amount && missedPaymentInfo.invoiceId) {
            return this.renderSecondMissedPaymentBanner(cancellationString, missedPaymentInfo.amount, missedPaymentInfo.invoiceId, brandName);
        } else if (now.isSameOrAfter(moment(invoiceDueDate)) && missedPaymentInfo.amount && missedPaymentInfo.invoiceId) {
            return this.renderFirstMissedPaymentBanner(secondAttemptString, cancellationString, missedPaymentInfo.amount, missedPaymentInfo.invoiceId, brandName);
        }

        return <></>
    }

    private renderFirstMissedPaymentBanner(secondAttemptString: string, cancellationString: string, amount: number, invoiceId: number, brandName: string): React.ReactNode {
        const paymentLinkLabel: string = `pay ${getAmountWithDecimalDigits(amount)} now,`;

        return (
            <WarningBanner id="firstMissedPayment" wording={
                <>
                    We didn't receive payment for your {brandName} renewal. You can{' '}
                    {this.renderPaymentLink(paymentLinkLabel, invoiceId)}{' '}
                    or we'll try the payment again <span className="font-weight-bold">{secondAttemptString}</span>.{' '}
                    If payment on the <span className="font-weight-bold">{secondAttemptString}</span> is unsuccessful,{' '}
                    we'll cancel your cover on <span className="font-weight-bold">{cancellationString}, 23:59</span>{' '}
                    (as per your terms and conditions).{' '}
                    {this.renderPaymentButton(amount, invoiceId)}
                </>
            }/>
        )
    }

    private renderSecondMissedPaymentBanner(cancellationString: string, amount: number, invoiceId: number, brandName: string): React.ReactNode {
        const paymentLinkLabel: string = `Pay ${getAmountWithDecimalDigits(amount)} now,`;

        return (
            <WarningBanner id="secondMissedPayment" wording={
                <>
                    We didn't receive payment for your {brandName} renewal.{' '}
                    {this.renderPaymentLink(paymentLinkLabel, invoiceId)}{' '}
                    or we'll cancel your {brandName} policy on the{' '}
                    <span className="font-weight-bold">{cancellationString}, 23:59</span> (as per your terms and conditions).
                    {this.renderPaymentButton(amount, invoiceId)}
                </>
            }/>
        )
    }

    private renderPaymentLink(label: string, invoiceId: number): React.ReactNode {
        return (
            <MissedPaymentLink
                policyNumber={this.props.policy.policyNumber}
                invoiceId={invoiceId}
                label={label}
                cssClass="muli-bold-16"
                id="payNow-inline"
                reuseCard={this.props.reuseCard}
            />
        )
    }

    private renderPaymentButton(amount: number, invoiceId: number): React.ReactNode {
        return (
            <div>
                <MissedPaymentLink
                    policyNumber={this.props.policy.policyNumber}
                    invoiceId={invoiceId}
                    label={`Pay ${getAmountWithDecimalDigits(amount)} now`}
                    cssClass="btn btn-white-dashboard"
                    id="payNow"
                    reuseCard={this.props.reuseCard}
                />
            </div>
        )
    }
}
